import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { useSelector } from "react-redux";
import "./Menu.css";
import { toast } from "react-hot-toast";
import TableService from "../../../Services/QRCodeServices/TableSerivce/TableService";

export const MenuItemDetail = ({ item, onClose, selectedTable }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [orgCode, setOrgCode] = useState(null);
  const [localQuantity, setLocalQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderedItems, setOrderedItems] = useState([]);
  const [menuOrderId, setMenuOrderId] = useState(null);

  useEffect(() => {
    const storedUserInfo = localStorage.getItem("user");
    if (storedUserInfo) {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(parsedUserInfo);
      setOrgCode(parsedUserInfo?.organizationInfo?.orgCode || null);
    }
  }, []);

  useEffect(() => {
    if (selectedTable) {
      fetchOrderedItems();
    }
  }, [selectedTable]);

  const fetchOrderedItems = () => {
    try {
      const storedData = JSON.parse(localStorage.getItem("orders")) || [];
      const tableOrder = storedData.find(
        (order) => order.tableID === selectedTable.id
      );
      if (tableOrder) {
        setOrderedItems(tableOrder.items);
      } else {
        setOrderedItems([]);
      }
    } catch (error) {
      console.error("Error fetching ordered items from localStorage:", error);
      toast.error("Failed to fetch ordered items");
    }
  };

  const saveToLocalStorage = (items) => {
    let storedData = JSON.parse(localStorage.getItem("orders")) || [];
    const tableIndex = storedData.findIndex(
      (order) => order.tableID === selectedTable.id
    );

    const updatedTableOrder = {
      tableID: selectedTable.id,
      items: items,
    };

    if (tableIndex !== -1) {
      // Update existing table order
      storedData[tableIndex] = updatedTableOrder;
    } else {
      // Add new table order
      storedData.push(updatedTableOrder);
    }

    localStorage.setItem("orders", JSON.stringify(storedData));
  };

  const calculateDiscountedPrice = () => {
    const price = item.price;
    const discount = item.discountDetail?.discountPercentage || 0;
    return price - (price * discount) / 100;
  };

  const calculateTotalPrice = () => {
    return calculateDiscountedPrice() * localQuantity;
  };

  const handleIncrement = () => {
    setLocalQuantity((prev) => prev + 1);
  };

  const handleDecrement = () => {
    setLocalQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handlePlaceOrder = async () => {
    if (!selectedTable) {
      toast.error("Please select a table before placing an order.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch current API items for the selected table
      const activeTables = await TableService.getActiveTableList();
      const tableData = activeTables.find((t) => t.id === selectedTable.id);
      let apiItems = [];
      if (tableData?.menuOrder?.menuOrderDetails) {
        apiItems = tableData.menuOrder.menuOrderDetails.map((detail) => ({
          menuItemId: detail.menuItemDetail?.id || detail.menuItemId,
        }));
      }

      // Check if the item exists in API items
      const existsInApi = apiItems.some(
        (apiItem) => apiItem.menuItemId === item.id
      );
      if (existsInApi) {
        toast.error(
          "This item is already part of the order. Please update the quantity in the order screen."
        );
        return;
      }

      // Proceed with local items check
      const discountedPrice = calculateDiscountedPrice();
      const totalPrice = discountedPrice * localQuantity;

      const imageUrl =
        item?.cloudR2ImagePath?.getCDNUrl() ||
        "https://via.placeholder.com/150";

      const newItem = {
        menuItemId: item.id,
        quantity: localQuantity,
        price: discountedPrice,
        totalPrice: totalPrice,
        title: item.title,
        imageUrl: imageUrl,
        timestamp: new Date().toISOString(),
      };

      let updatedItems;
      if (orderedItems.length > 0) {
        updatedItems = [...orderedItems];
        const existingItemIndex = updatedItems.findIndex(
          (orderItem) => orderItem.menuItemId === item.id
        );

        if (existingItemIndex !== -1) {
          updatedItems[existingItemIndex].quantity += localQuantity;
          updatedItems[existingItemIndex].totalPrice += totalPrice;
        } else {
          updatedItems.push(newItem);
        }

        toast.success("Order added successfully!");
      } else {
        updatedItems = [newItem];
        toast.success("Order updated successfully!");
      }

      saveToLocalStorage(updatedItems);
      setOrderedItems(updatedItems);
      onClose();
    } catch (error) {
      console.error("Error processing order:", error);
      setError(error.message || "Failed to process order.");
      toast.error("Failed to process order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center menu-item-detail-pos mb-3">
        <img
          src={
            item?.cloudR2ImagePath?.getCDNUrl() ||
            "https://via.placeholder.com/150"
          }
          alt={item.title}
          className="img-fluid"
        />
      </div>
      <div className="mb-3">
        <h4 className="fw-bold">{item.title}</h4>
        <p className="text-muted">
          {item.description || "No description available."}
        </p>
      </div>
      <div className="mb-3">
        {item.discountDetail && (
          <p className="mb-1 text-muted">
            <span className="fw-bold">Original Price:</span> Rs. {item.price}
          </p>
        )}
        {item.discountDetail && (
          <p className="mb-1 text-danger">
            <span className="fw-bold">Discount:</span>{" "}
            {item.discountDetail.discountPercentage}% OFF
          </p>
        )}
        <p className="mb-1">
          <span className="fw-bold">Price:</span> Rs.{" "}
          {calculateDiscountedPrice()}
        </p>
      </div>
      <div className="d-flex align-items-center gap-3 mb-3">
        <Button color="light" size="sm" onClick={handleDecrement}>
          -
        </Button>
        <span className="fw-bold">{localQuantity}</span>
        <Button color="light" size="sm" onClick={handleIncrement}>
          +
        </Button>
      </div>
      <div className="mb-3">
        <p className="fw-bold">Total Price: Rs. {calculateTotalPrice()}</p>
      </div>
      <div className="text-center">
        <Button
          color="primary"
          size="lg"
          className="w-100"
          onClick={handlePlaceOrder}
          disabled={loading}
        >
          {loading ? "Adding Order..." : "Add Order"}
        </Button>
        {error && <p className="text-danger mt-2">{error}</p>}
      </div>
    </div>
  );
};
